import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MotionConfig, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Testimonials } from "@/components/site/Testimonials";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import Home from "@/pages/Home";
import { ALIASES } from "@/data/landing";
import { HUB_SLUGS } from "@/data/countries";
// Route-level code splitting: only Home loads eagerly (playbook §3.5).
const lazyPage = (f) => lazy(f);
const Portfolio = lazyPage(() => import("@/pages/Portfolio"));
const Outsourcing = lazyPage(() => import("@/pages/Outsourcing"));
const Locations = lazyPage(() => import("@/pages/Locations"));
const Insights = lazyPage(() => import("@/pages/Insights"));
const ServiceAreas = lazyPage(() => import("@/pages/ServiceAreas"));
const Landing = lazyPage(() => import("@/pages/Landing"));
const County = lazyPage(() => import("@/pages/County"));
const StateService = lazyPage(() => import("@/pages/StateService"));
const Country = lazyPage(() => import("@/pages/Country"));
const About = lazyPage(() => import("@/pages/About"));
const Services = lazyPage(() => import("@/pages/Services"));
const ServiceDetail = lazyPage(() => import("@/pages/ServiceDetail"));
const Pricing = lazyPage(() => import("@/pages/Pricing"));
const Career = lazyPage(() => import("@/pages/Career"));
const GetStarted = lazyPage(() => import("@/pages/GetStarted"));
const Contact = lazyPage(() => import("@/pages/Contact"));
const Legal = lazyPage(() => import("@/pages/Legal"));
const Admin = lazyPage(() => import("@/pages/Admin"));
const NotFound = lazyPage(() => import("@/pages/NotFound"));
const Fallback = () => <div className="min-h-[60vh] bg-[#102A43]" aria-busy="true" />;

const SmoothScroll = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); window.__lenis = null; };
  }, []);
  useEffect(() => {
    if (hash) {
      // Lazy routes mount after navigation: retry briefly until the target exists, then scroll under the fixed header.
      let tries = 0;
      const go = () => {
        const el = document.getElementById(hash.slice(1));
        if (el) { if (window.__lenis) window.__lenis.scrollTo(el, { offset: -120 }); else el.scrollIntoView({ block: "start" }); return; }
        if (tries++ < 20) setTimeout(go, 100);
      };
      go();
      return;
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Page = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <motion.main key={pathname} initial={{ opacity: 0.4, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
      {children}
    </motion.main>
  );
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      <SmoothScroll />
      <a href="#content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] bg-white px-3 py-2" data-testid="skip-link">Skip to content</a>
      {!isAdmin && <Header />}
      <div id="content">
        <Page>
          <Suspense fallback={<Fallback />}><Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/career" element={<Career />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/outsourcing" element={<Outsourcing />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:slug" element={<Locations />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<Insights />} />
            <Route path="/service-areas" element={<ServiceAreas />} />
            <Route path="/service-areas/:slug" element={<ServiceAreas />} />
            <Route path="/service-areas/:state/county/:county" element={<County />} />
            <Route path="/service-areas/:state/:service" element={<StateService />} />
            {HUB_SLUGS.map((h) => <Route key={h} path={`/${h}`} element={<Country />} />)}
            {HUB_SLUGS.map((h) => <Route key={`${h}-sub`} path={`/${h}/:slug`} element={<Country />} />)}
            {HUB_SLUGS.map((h) => <Route key={`${h}-lang`} path={`/${h}/:slug/:lang`} element={<Country />} />)}
            {Object.entries(ALIASES).map(([from, to]) => <Route key={from} path={from} element={<Navigate to={to} replace />} />)}
            <Route path="/:slug" element={<Landing />} />
            <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
            <Route path="/terms-and-conditions" element={<Legal kind="terms" />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<NotFound />} />
          </Routes></Suspense>
        </Page>
      </div>
      {!isAdmin && <Testimonials />}
      {!isAdmin && <Footer />}
      <WhatsAppButton />
    </>
  );
};

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <Shell />
        <Toaster position="bottom-center" />
      </BrowserRouter>
    </MotionConfig>
  );
}

export default App;
